import type { components } from '@fluencelabs/vodopad-admin-api'

import client from '@/api/client'
import { unwrapApiData, unwrapApiResult } from '@/api/unwrapApiResult'

export type CatalogImage = components['schemas']['AdminImageDto']
export type NewImage = components['schemas']['NewImageDTO']
export type ImageLabels = components['schemas']['ImageLabelsPatch']
export const IMAGES_KEY = ['image-catalog'] as const

export async function fetchImages(signal?: AbortSignal) {
  return unwrapApiData(
    await client.GET('/v1/admin/default_images', { signal }),
    'Could not load images',
  )
}

export async function createImage(body: NewImage) {
  return unwrapApiData(
    await client.POST('/v1/admin/default_images', { body }),
    'Could not create image',
  )
}

export async function updateLabels(id: string, body: ImageLabels) {
  return unwrapApiData(
    await client.PATCH('/v1/admin/default_images/{image_id}', {
      params: { path: { image_id: id } },
      body,
    }),
    'Could not update image',
  )
}

export type ImageAction = 'publish' | 'hide' | 'default' | 'remove'
export async function actOnImage(
  id: string,
  action: ImageAction,
  replace?: string,
) {
  const params = { path: { image_id: id } }
  if (action === 'remove') {
    unwrapApiResult(
      await client.DELETE('/v1/admin/default_images/{image_id}', {
        params,
        parseAs: 'text',
      }),
      'Could not remove image',
    )
    return
  }
  if (action === 'publish') {
    return unwrapApiData(
      await client.POST('/v1/admin/default_images/{image_id}/publish', {
        params,
        body: { replaceImageId: replace },
      }),
      'Could not publish image',
    )
  }
  if (action === 'hide') {
    return unwrapApiData(
      await client.POST('/v1/admin/default_images/{image_id}/hide', { params }),
      'Could not hide image',
    )
  }
  return unwrapApiData(
    await client.POST('/v1/admin/default_images/{image_id}/default', {
      params,
    }),
    'Could not set default image',
  )
}

export async function probeSize(downloadUrl: string) {
  return unwrapApiData(
    await client.POST('/v1/admin/image_metadata/probe', {
      body: { downloadUrl },
    }),
    'Could not inspect image size',
  )
}
